import React, { FC, useCallback, useState } from 'react';
import { useDataTableColumnConfigurations } from 'utils/hooks/useDataTableColumnConfigurations/useDataTableColumnConfigurations';
import { useStaticPermissionsByName } from 'common/auth/Authorize';
import { TerminationReasonModel } from '@utmost.co/enterprise-models';
import { DataTable, DataTableToolbar, DataTablePaginator } from '@utmost.co/utmost-design-system';
import { useTerminationReasonsColumns, TerminationReasonTypeColumn } from './useTerminationReasonsColumns';
import { useTerminationReasons } from './hooks/useTerminationReasons';
import { TerminationReasonsDeleteModal } from './TerminationReasonsDeleteModal';
import { TerminationReasonsAddModal } from './TerminationReasonsAddModal';
import { useTerminationReasonsIntl } from './translations/useTerminationReasonsIntl';

export const TerminationReasonsPage: FC<React.PropsWithChildren<unknown>> = () => {
  const intl = useTerminationReasonsIntl();
  const { data, fetchTerminationReasons, loading } = useTerminationReasons();
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [addTerminationReasonModel, setAddTerminationReasonModel] = useState<boolean>(false);
  const [terminationReason, setTerminationReason] = useState<TerminationReasonModel | null>(null);
  const permissions = useStaticPermissionsByName(['REFERENCE_DATA']);
  const { columnConfiguration, patchColumnConfiguration } = useDataTableColumnConfigurations('TERMINATION_REASONS');

  const totalCount = data?.terminationReasons.totalCount || 0;

  const handleCloseModal = useCallback(() => {
    setSelectedUid(null);
    setTerminationReason(null);
    setAddTerminationReasonModel(false);
  }, []);

  const handleAddTerminationReason = useCallback(() => {
    setAddTerminationReasonModel(true);
  }, []);

  const handleAddTerminationReasonDone = useCallback(() => {
    handleCloseModal();
    fetchTerminationReasons(0);
  }, [handleCloseModal, fetchTerminationReasons]);

  const handleTerminationReasonRemoved = useCallback(() => {
    handleCloseModal();
    fetchTerminationReasons(0);
  }, [handleCloseModal, fetchTerminationReasons]);

  const columns = useTerminationReasonsColumns({
    onDelete: setSelectedUid,
    onEdit: setTerminationReason,
    terminationReasons: data?.terminationReasons.items
  });

  if (permissions.REFERENCE_DATA.READ === false)
    return <span data-test="no-permissions">{intl.youDoNotHavePermission}</span>;

  return (
    <>
      {selectedUid && (
        <TerminationReasonsDeleteModal
          uid={selectedUid}
          onDone={handleTerminationReasonRemoved}
          onClose={handleCloseModal}
        />
      )}
      {(addTerminationReasonModel || terminationReason) && (
        <TerminationReasonsAddModal
          onClose={handleCloseModal}
          onDone={handleAddTerminationReasonDone}
          terminationReason={terminationReason}
        />
      )}
      <DataTable<TerminationReasonModel, TerminationReasonTypeColumn, { search: string }>
        totalCount={totalCount}
        isFetching={loading}
        columns={columns}
        onColumnConfigurationChanged={patchColumnConfiguration}
        initialState={columnConfiguration}
        dataTest="termination-reasons__page__container"
        data={data?.terminationReasons.items ?? []}
      >
        <DataTableToolbar
          addButtonProps={{ onAdd: handleAddTerminationReason, disabled: false }}
          message={intl.viewingResults({
            totalCount,
            pageDataCount: data?.terminationReasons.items?.length ?? 0
          })}
        />

        <DataTablePaginator fetchNextPage={({ pageIndex }) => fetchTerminationReasons(pageIndex)} />
      </DataTable>
    </>
  );
};
